import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Outlet, useLocation, useNavigate } from "react-router";
import { authApi } from "@/core/api/authApi";
import { crmApi } from "@/core/api/crmApi";
import { leadsApi } from "@/core/api/leadsApi";
import { tenantWorkspaceApi } from "@/core/api/tenantWorkspaceApi";
import { tenantFeaturesApi } from "@/core/api/tenantFeaturesApi";
import { hasPermission } from "@/core/auth/permissions";
import { useAuthStore } from "@/core/auth/authStore";
import { useUiStore } from "@/core/auth/uiStore";
import { queryClient } from "@/core/query/queryClient";
import { AppIcon, type AppIconName } from "@/shared/components/AppIcon";
import { TenantNotificationBell } from "@/features/notifications/components/TenantNotificationBell";
import { GlobalCrmSearch } from "@/features/search/components/GlobalCrmSearch";
import { QuickLeadModal } from "@/features/leads/components/QuickLeadModal";
import { GuidanceAssistant } from "@/features/guidance/components/GuidanceAssistant";
import { tenantBusiness } from "@/core/utils/tenantBusiness";
import {
  tenantFeatureCodes,
  type TenantFeatureCode,
} from "@/core/types/features";

type NavigationGroupId =
  | "workspace"
  | "sales"
  | "commerce"
  | "restaurant"
  | "marketing"
  | "company"
  | "settings"
  | "support";

interface NavigationItem {
  label: string;
  path: string;
  icon: AppIconName;
  group: NavigationGroupId;
  managerOnly?: boolean;
  permission?: string;
  feature?: TenantFeatureCode;
  features?: TenantFeatureCode[];
}

interface NavigationGroup {
  id: NavigationGroupId;
  label: string;
  icon: AppIconName;
}

const navigationGroups: NavigationGroup[] = [
  { id: "workspace", label: "میز کار", icon: "grid" },
  { id: "sales", label: "فروش و مشتریان", icon: "trend" },
  { id: "commerce", label: "فروشگاه", icon: "box" },
  { id: "restaurant", label: "رستوران", icon: "calendar" },
  { id: "marketing", label: "بازاریابی و ارتباطات", icon: "send" },
  { id: "company", label: "مدیریت شرکت", icon: "building" },
  { id: "settings", label: "تنظیمات", icon: "settings" },
  { id: "support", label: "پشتیبانی", icon: "sparkles" },
];

const navigation = (entityName: string): NavigationItem[] => [
  { label: "نمای کلی", path: "/dashboard", icon: "grid", group: "workspace" },
  { label: "راهنمای استفاده", path: "/guides", icon: "sparkles", group: "workspace" },
  {
    label: "سرنخ‌ها",
    path: "/leads",
    icon: "trend",
    group: "sales",
    permission: "leads.read",
    feature: tenantFeatureCodes.leads,
  },
  {
    label: "هوشمندی لید و نمایشگاه",
    path: "/leads/intelligence",
    icon: "sparkles",
    group: "sales",
    managerOnly: true,
    feature: tenantFeatureCodes.leads,
  },
  {
    label: "مخاطبان",
    path: "/contacts",
    icon: "user",
    group: "sales",
    permission: "contacts.read",
    feature: tenantFeatureCodes.customers,
  },
  {
    label: "سازمان‌ها",
    path: "/accounts",
    icon: "building",
    group: "sales",
    permission: "accounts.read",
    feature: tenantFeatureCodes.organizations,
  },
  {
    label: "پرسنل و تیم‌ها",
    path: "/team",
    icon: "users",
    group: "company",
    permission: "staff.manage",
    feature: tenantFeatureCodes.organizations,
  },
  {
    label: "نقش‌ها و دسترسی‌ها",
    path: "/roles",
    icon: "settings",
    group: "company",
    permission: "staff.manage",
    feature: tenantFeatureCodes.organizations,
  },
  {
    label: `پروفایل ${entityName}`,
    path: "/organization",
    icon: "building",
    group: "company",
    managerOnly: true,
    feature: tenantFeatureCodes.organizations,
  },
  {
    label: `کاتالوگ و فایل‌های ${entityName}`,
    path: "/catalog",
    icon: "box",
    group: "commerce",
    managerOnly: true,
    feature: tenantFeatureCodes.products,
  },
  {
    label: "میز فروش سریع",
    path: "/retail",
    icon: "send",
    group: "commerce",
    permission: "contacts.create",
    features: [
      tenantFeatureCodes.products,
      tenantFeatureCodes.customers,
      tenantFeatureCodes.customerInterest,
      tenantFeatureCodes.personalLanding,
      tenantFeatureCodes.sms,
    ],
  },
  {
    label: "سفارش و صندوق رستوران",
    path: "/restaurant",
    icon: "check",
    group: "restaurant",
    permission: "contacts.create",
    features: [
      tenantFeatureCodes.restaurantPos,
      tenantFeatureCodes.products,
      tenantFeatureCodes.customers,
      tenantFeatureCodes.customerInterest,
    ],
  },
  {
    label: "مدیریت منوی رستوران",
    path: "/restaurant/menu",
    icon: "box",
    group: "restaurant",
    managerOnly: true,
    features: [tenantFeatureCodes.restaurantPos, tenantFeatureCodes.products],
  },
  {
    label: "تنظیمات رزرو",
    path: "/reservation-settings",
    icon: "bell",
    group: "settings",
    managerOnly: true,
    feature: tenantFeatureCodes.personalLanding,
  },
  {
    label: "تنظیمات فروش سریع",
    path: "/retail-settings",
    icon: "settings",
    group: "settings",
    managerOnly: true,
    feature: tenantFeatureCodes.products,
  },
  {
    label: "باشگاه مشتریان",
    path: "/loyalty",
    icon: "sparkles",
    group: "commerce",
    managerOnly: true,
    features: [tenantFeatureCodes.products, tenantFeatureCodes.customers],
  },
  {
    label: "کمپین‌ها",
    path: "/campaigns",
    icon: "activity",
    group: "marketing",
    permission: "campaigns.read",
    feature: tenantFeatureCodes.campaigns,
  },
  {
    label: "ارسال خودکار",
    path: "/automations",
    icon: "send",
    group: "marketing",
    managerOnly: true,
    feature: tenantFeatureCodes.campaigns,
  },
  {
    label: "ارسال پیام به مشتریان",
    path: "/customer-sms",
    icon: "send",
    group: "marketing",
    managerOnly: true,
    feature: tenantFeatureCodes.sms,
  },
  {
    label: "پشتیبانی پلتفرم",
    path: "/support",
    icon: "send",
    group: "support",
    permission: "tenancy.read",
  },
  {
    label: "اشتراک و شارژ پنل",
    path: "/billing",
    icon: "document",
    group: "settings",
    permission: "tenancy.manage",
  },
  {
    label: "فعالیت‌ها",
    path: "/activities",
    icon: "calendar",
    group: "sales",
    permission: "activities.read",
    feature: tenantFeatureCodes.pipelines,
  },
  {
    label: "فرصت‌های فروش",
    path: "/opportunities",
    icon: "activity",
    group: "sales",
    permission: "opportunities.read",
    feature: tenantFeatureCodes.pipelines,
  },
  {
    label: "فروش سازمانی و نمایشگاهی",
    path: "/b2b-sales",
    icon: "building",
    group: "sales",
    permission: "opportunities.read",
    feature: tenantFeatureCodes.quotation,
  },
];

const titles = (entityName: string): Record<string, string> => ({
  "/dashboard": "نمای کلی",
  "/guides": "راهنمای استفاده",
  "/leads": "مدیریت سرنخ‌ها",
  "/leads/intelligence": "هوشمندی لید و نمایشگاه",
  "/contacts": "مخاطبان",
  "/accounts": "سازمان‌ها",
  "/team": "پرسنل و تیم‌ها",
  "/roles": "نقش‌ها و دسترسی‌ها",
  "/organization": `پروفایل ${entityName}`,
  "/catalog": `کاتالوگ و فایل‌های ${entityName}`,
  "/retail": "میز فروش سریع",
  "/restaurant/menu": "مدیریت منوی رستوران",
  "/restaurant": "سفارش و صندوق رستوران",
  "/reservation-settings": "تنظیمات رزرو",
  "/retail-settings": "تنظیمات فروش سریع",
  "/loyalty": "باشگاه مشتریان",
  "/campaigns": "مدیریت کمپین‌ها",
  "/automations": "ارسال خودکار",
  "/customer-sms": "ارسال پیام به مشتریان",
  "/support": "پشتیبانی پلتفرم",
  "/billing": "اشتراک و شارژ پنل",
  "/activities": "فعالیت‌ها",
  "/opportunities": "فرصت‌های فروش",
  "/b2b-sales": "فروش سازمانی و نمایشگاهی",
  "/profile": "پروفایل من",
});

const initials = (value?: string): string =>
  value
    ?.trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("") || "ک";

const isNavigationPathActive = (pathname: string, itemPath: string): boolean =>
  pathname === itemPath ||
  (itemPath !== "/dashboard" && pathname.startsWith(`${itemPath}/`));

export const AppShell = (): React.ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const [quickLeadOpen, setQuickLeadOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [expandedMobileGroup, setExpandedMobileGroup] = useState<NavigationGroupId | null>("workspace");
  const me = useAuthStore((state) => state.me);
  const tenant = useAuthStore((state) => state.tenant);
  const role = useAuthStore((state) => state.role);
  const permissions = useAuthStore((state) => state.permissions);
  const clearSession = useAuthStore((state) => state.clearSession);
  const colorMode = useUiStore((state) => state.colorMode);
  const toggleColorMode = useUiStore((state) => state.toggleColorMode);
  const featuresQuery = useQuery({
    queryKey: ["tenant-features"],
    queryFn: tenantFeaturesApi.current,
    enabled: Boolean(tenant),
  });
  const profileQuery = useQuery({
    queryKey: ["tenant-workspace", "profile"],
    queryFn: tenantWorkspaceApi.profile,
    enabled: Boolean(tenant),
  });
  const subscriptionQuery = useQuery({
    queryKey: ["tenant-workspace", "subscription"],
    queryFn: tenantWorkspaceApi.subscription,
    enabled: role === "manager",
  });
  const enabledFeatures = useMemo(
    () =>
      new Set(
        featuresQuery.data
          ? featuresQuery.data
              .filter((feature) => feature.isEnabled)
              .map((feature) => feature.code)
          : Object.values(tenantFeatureCodes),
      ),
    [featuresQuery.data],
  );
  const canReadAccounts = hasPermission(permissions, "accounts.read");
  const accountsQuery = useQuery({
    queryKey: ["accounts", "capacity-count"],
    queryFn: () => crmApi.accounts({ page: 1, pageSize: 1 }),
    enabled:
      role === "manager" &&
      canReadAccounts &&
      enabledFeatures.has(tenantFeatureCodes.organizations),
  });
  const leadCountQuery = useQuery({
    queryKey: ["leads", "navigation-count"],
    queryFn: () => leadsApi.list({ page: 1, pageSize: 1 }),
    enabled:
      hasPermission(permissions, "leads.read") &&
      enabledFeatures.has(tenantFeatureCodes.leads),
  });
  const customerCount = accountsQuery.data?.totalCount;
  const maximumCustomers = subscriptionQuery.data?.maxCustomerAccounts;
  const business = tenantBusiness(profileQuery.data?.business);
  const entityName = business.entityName;
  const usedPercent =
    maximumCustomers && customerCount !== undefined
      ? Math.min(100, Math.round((customerCount / maximumCustomers) * 100))
      : 0;
  const usageText =
    !canReadAccounts
      ? "آمار مشتری خارج از سطح دسترسی شماست."
      : subscriptionQuery.isLoading || accountsQuery.isLoading
        ? "در حال دریافت مصرف واقعی اشتراک…"
        : subscriptionQuery.isError || accountsQuery.isError
          ? "دریافت وضعیت اشتراک ناموفق بود."
          : maximumCustomers === 0
            ? `${new Intl.NumberFormat("fa-IR").format(customerCount ?? 0)} مشتری ثبت‌شده · ظرفیت نامحدود`
            : `${new Intl.NumberFormat("fa-IR").format(customerCount ?? 0)} از ${new Intl.NumberFormat("fa-IR").format(maximumCustomers ?? 0)} مشتری · ${new Intl.NumberFormat("fa-IR").format(usedPercent)}٪ مصرف`;
  const items = useMemo(
    () =>
      navigation(entityName).filter(
        (item) =>
          (!item.managerOnly || role === "manager") &&
          (!item.permission || hasPermission(permissions, item.permission)) &&
          (!item.feature || enabledFeatures.has(item.feature)) &&
          (!item.features ||
            item.features.every((feature) => enabledFeatures.has(feature))),
      ),
    [enabledFeatures, entityName, permissions, role],
  );
  const groupedItems = useMemo(
    () =>
      navigationGroups
        .map((group) => ({
          ...group,
          items: items.filter((item) => item.group === group.id),
        }))
        .filter((group) => group.items.length > 0),
    [items],
  );
  const activeGroupId = useMemo<NavigationGroupId>(
    () =>
      groupedItems.find((group) =>
        group.items.some((item) =>
          isNavigationPathActive(location.pathname, item.path),
        ),
      )?.id ?? "workspace",
    [groupedItems, location.pathname],
  );

  useEffect(() => {
    if (mobileMenuOpen) setExpandedMobileGroup(activeGroupId);
  }, [activeGroupId, mobileMenuOpen]);

  const currentTitle =
    Object.entries(titles(entityName)).find(([path]) =>
      location.pathname.startsWith(path),
    )?.[1] || "CRM";
  const canCreateLead =
    hasPermission(permissions, "leads.create") &&
    enabledFeatures.has(tenantFeatureCodes.leads);
  const canUseRetailDesk =
    hasPermission(permissions, "contacts.create") &&
    [
      tenantFeatureCodes.products,
      tenantFeatureCodes.customers,
      tenantFeatureCodes.customerInterest,
      tenantFeatureCodes.personalLanding,
      tenantFeatureCodes.sms,
    ].every((feature) => enabledFeatures.has(feature));
  const mobileWorkPath =
    hasPermission(permissions, "activities.read") &&
    enabledFeatures.has(tenantFeatureCodes.pipelines)
      ? "/activities"
      : hasPermission(permissions, "leads.read") &&
          enabledFeatures.has(tenantFeatureCodes.leads)
        ? "/leads"
        : "/retail";
  const mobileCustomersPath =
    hasPermission(permissions, "contacts.read") &&
    enabledFeatures.has(tenantFeatureCodes.customers)
      ? "/contacts"
      : hasPermission(permissions, "leads.read") &&
          enabledFeatures.has(tenantFeatureCodes.leads)
        ? "/leads"
        : "/retail";
  const goTo = (path: string): void => {
    navigate(path);
    setMobileMenuOpen(false);
  };
  const logout = async (): Promise<void> => {
    try {
      await authApi.logout();
    } finally {
      queryClient.clear();
      clearSession();
      navigate("/login", { replace: true });
    }
  };

  const renderNavigationItem = (item: NavigationItem, mobile: boolean): React.ReactElement => {
    const active = isNavigationPathActive(location.pathname, item.path);
    return (
      <button
        className={`flex w-full items-center gap-3 rounded-xl px-3 text-right text-sm font-bold transition ${mobile ? "min-h-11 py-2.5" : "py-2.5"} ${active ? "bg-brand-50 text-brand-600 shadow-sm" : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"}`}
        key={item.path}
        onClick={() => goTo(item.path)}
        type="button"
      >
        <AppIcon name={item.icon} size={18} />
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        {item.path === "/leads" && leadCountQuery.data ? (
          <span className="mr-auto shrink-0 rounded-full bg-white px-2 py-0.5 text-[11px] text-brand-600 shadow-sm">
            {new Intl.NumberFormat("fa-IR").format(
              leadCountQuery.data.totalCount,
            )}
          </span>
        ) : null}
      </button>
    );
  };

  const renderSidebar = (mobile: boolean): React.ReactElement => (
    <aside
      className={`flex h-full flex-col overflow-hidden border-l border-slate-200 bg-white shadow-[0_0_32px_rgba(24,38,76,0.08)] ${mobile ? "w-[min(88vw,22rem)] px-4 pb-[max(env(safe-area-inset-bottom),1rem)] pt-[max(env(safe-area-inset-top),1rem)]" : "w-72 px-4 py-5"}`}
      aria-label={mobile ? "منوی اصلی موبایل" : "منوی اصلی"}
    >
      <div className="flex items-center gap-2 px-1">
        <button
          className="flex min-w-0 flex-1 items-center gap-3 px-1 text-right"
          onClick={() => goTo("/dashboard")}
          type="button"
        >
          <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-bl from-brand-500 to-violet-500 text-lg font-black text-white shadow-lg shadow-brand-500/25">
            C
          </span>
          <span className="min-w-0">
            <span className="block truncate text-base font-black text-ink-950">
              همکار CRM
            </span>
            <span className="mt-0.5 block line-clamp-2 text-xs leading-5 text-slate-400">
              فضای فروش {entityName} و ارتباط با مشتری
            </span>
          </span>
        </button>
        {mobile ? (
          <button
            aria-label="بستن منو"
            className="grid size-10 shrink-0 place-items-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-50 active:scale-95"
            onClick={() => setMobileMenuOpen(false)}
            type="button"
          >
            <AppIcon name="close" size={19} />
          </button>
        ) : null}
      </div>

      <div className={`${mobile ? "my-4" : "my-5"} h-px bg-slate-100`} />

      {mobile ? (
        <nav
          className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain pb-4 pr-0.5"
          aria-label="دسته‌بندی منوی موبایل"
        >
          {groupedItems.map((group) => {
            const expanded = expandedMobileGroup === group.id;
            const groupActive = group.id === activeGroupId;
            return (
              <section key={group.id}>
                <button
                  aria-expanded={expanded}
                  className={`flex min-h-12 w-full items-center justify-between rounded-2xl px-3 py-2.5 text-right transition active:scale-[0.99] ${groupActive ? "bg-brand-50 text-brand-700" : "text-slate-600 hover:bg-slate-50"}`}
                  onClick={() =>
                    setExpandedMobileGroup((current) =>
                      current === group.id ? null : group.id,
                    )
                  }
                  type="button"
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span
                      className={`grid size-9 shrink-0 place-items-center rounded-xl ${groupActive ? "bg-white text-brand-600 shadow-sm" : "bg-slate-50 text-slate-500"}`}
                    >
                      <AppIcon name={group.icon} size={18} />
                    </span>
                    <span className="truncate text-sm font-black">
                      {group.label}
                    </span>
                  </span>
                  <AppIcon
                    className={`shrink-0 transition-transform duration-200 ${expanded ? "-rotate-90" : ""}`}
                    name="chevron-left"
                    size={17}
                  />
                </button>
                {expanded ? (
                  <div className="mr-4 mt-1.5 space-y-1 border-r border-slate-100 pr-2">
                    {group.items.map((item) => renderNavigationItem(item, true))}
                  </div>
                ) : null}
              </section>
            );
          })}
        </nav>
      ) : (
        <nav
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pb-3 pr-0.5"
          aria-label="دسته‌بندی منوی اصلی"
        >
          {groupedItems.map((group) => (
            <section key={group.id}>
              <div className="flex items-center gap-2 px-3 text-[11px] font-black tracking-[0.04em] text-slate-400">
                <AppIcon name={group.icon} size={14} />
                <span>{group.label}</span>
              </div>
              <div className="mt-1.5 space-y-1">
                {group.items.map((item) => renderNavigationItem(item, false))}
              </div>
            </section>
          ))}
        </nav>
      )}

      {role === "manager" ? (
        <div className={`shrink-0 rounded-2xl bg-slate-950 p-4 text-white ${mobile ? "mt-2" : "mt-4"}`}>
          <div className="flex items-center gap-2 text-sm font-bold">
            <AppIcon name="sparkles" size={18} /> پلن{" "}
            {subscriptionQuery.isLoading
              ? "…"
              : subscriptionQuery.data?.planName || "—"}
          </div>
          <p className="mt-2 text-xs leading-6 text-slate-300">{usageText}</p>
          <button
            className="mt-3 min-h-10 w-full rounded-xl bg-white/10 px-3 py-2 text-xs font-bold transition hover:bg-white/20 active:scale-[0.99]"
            onClick={() => goTo("/organization")}
            type="button"
          >
            مشاهده وضعیت اشتراک
          </button>
        </div>
      ) : null}
    </aside>
  );

  return (
    <div
      className="crm-shell min-h-screen bg-[#f7f8fc] text-slate-900"
      dir="rtl"
    >
      <div className="fixed inset-y-0 right-0 z-40 hidden xl:block">
        {renderSidebar(false)}
      </div>
      {mobileMenuOpen ? (
        <button
          aria-label="بستن منو"
          className="fixed inset-0 z-[70] bg-slate-950/40 backdrop-blur-[1px] xl:hidden"
          onClick={() => setMobileMenuOpen(false)}
          type="button"
        />
      ) : null}
      <div
        className={`fixed inset-y-0 right-0 z-[80] transition-transform duration-300 xl:hidden ${mobileMenuOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        {renderSidebar(true)}
      </div>
      <div className="min-h-screen xl:pr-72">
        <header className="sticky top-0 z-30 flex h-[74px] items-center justify-between border-b border-slate-200/80 bg-white/90 px-4 backdrop-blur md:px-7">
          <div className="flex min-w-0 items-center gap-3">
            <button
              aria-label="بازکردن منو"
              className="grid size-10 place-items-center rounded-xl border border-slate-200 text-slate-600 xl:hidden"
              onClick={() => setMobileMenuOpen(true)}
              type="button"
            >
              <AppIcon name="menu" />
            </button>
            <div className="min-w-0">
              <p className="truncate text-base font-black text-ink-950 md:text-lg">
                {currentTitle}
              </p>
              <p className="hidden truncate text-xs text-slate-400 sm:block">
                {tenant?.name || `${entityName} شما`} · امروز برای رشد فروش
                آماده‌اید
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <GlobalCrmSearch
              canSearchAccounts={
                hasPermission(permissions, "accounts.read") &&
                enabledFeatures.has(tenantFeatureCodes.organizations)
              }
              canSearchContacts={
                hasPermission(permissions, "contacts.read") &&
                enabledFeatures.has(tenantFeatureCodes.customers)
              }
              canSearchLeads={
                hasPermission(permissions, "leads.read") &&
                enabledFeatures.has(tenantFeatureCodes.leads)
              }
              className="hidden md:block"
            />
            <button
              aria-label="جست‌وجو در CRM"
              className="grid size-10 shrink-0 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 md:hidden"
              onClick={() => setMobileSearchOpen(true)}
              type="button"
            >
              <AppIcon name="search" size={18} />
            </button>
            <button
              aria-label={
                colorMode === "dark"
                  ? "فعال‌کردن حالت روشن"
                  : "فعال‌کردن حالت تاریک"
              }
              className="grid size-10 shrink-0 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50"
              onClick={toggleColorMode}
              title={colorMode === "dark" ? "حالت روشن" : "حالت تاریک"}
              type="button"
            >
              <AppIcon name={colorMode === "dark" ? "sun" : "moon"} size={18} />
            </button>
            <TenantNotificationBell />
            <div className="relative">
              <button
                className="flex items-center gap-2 rounded-xl py-1 pr-1 text-right transition hover:bg-slate-50 sm:pl-2"
                onClick={() => setUserMenuOpen((value) => !value)}
                type="button"
              >
                <span className="grid size-9 place-items-center rounded-xl bg-brand-100 text-xs font-black text-brand-600">
                  {initials(me?.membership.displayName)}
                </span>
                <span className="hidden sm:block">
                  <span className="block max-w-32 truncate text-xs font-bold">
                    {me?.membership.displayName || "کاربر CRM"}
                  </span>
                  <span className="block text-[11px] text-slate-400">
                    {role === "manager" ? `مدیر ${entityName}` : "کارشناس فروش"}
                  </span>
                </span>
              </button>
              {userMenuOpen ? (
                <div className="absolute left-0 top-12 z-50 w-44 rounded-2xl border border-slate-100 bg-white p-2 shadow-xl shadow-slate-900/10">
                  <button
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-right text-sm font-bold text-slate-600 hover:bg-slate-50"
                    onClick={() => {
                      setUserMenuOpen(false);
                      goTo("/profile");
                    }}
                    type="button"
                  >
                    <AppIcon name="user" size={17} /> پروفایل من
                  </button>
                  <button
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-right text-sm font-bold text-rose-600 hover:bg-rose-50"
                    onClick={() => void logout()}
                    type="button"
                  >
                    <AppIcon name="close" size={17} /> خروج از حساب
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1720px] p-4 pb-28 sm:p-6 sm:pb-9 lg:p-8">
          <Outlet />
        </main>
      </div>
      <nav aria-label="منوی سریع موبایل" className="fixed inset-x-0 bottom-0 z-[60] grid grid-cols-5 border-t border-slate-200 bg-white/95 px-2 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-2 shadow-[0_-8px_30px_rgba(15,23,42,0.08)] backdrop-blur xl:hidden">
        <button className={`flex min-h-14 flex-col items-center justify-center gap-1 rounded-xl text-[10px] font-bold ${location.pathname === "/dashboard" ? "text-brand-600" : "text-slate-500"}`} onClick={() => goTo("/dashboard")} type="button">
          <AppIcon name="grid" size={20} />
          خانه
        </button>
        <button className={`flex min-h-14 flex-col items-center justify-center gap-1 rounded-xl text-[10px] font-bold ${location.pathname.startsWith(mobileWorkPath) ? "text-brand-600" : "text-slate-500"}`} onClick={() => goTo(mobileWorkPath)} type="button">
          <AppIcon name="calendar" size={20} />
          پیگیری‌ها
        </button>
        <button aria-label="اقدام سریع" className="-mt-7 mx-auto grid size-14 place-items-center rounded-2xl bg-brand-600 text-white shadow-lg shadow-brand-500/35 transition hover:bg-brand-700 active:scale-95" onClick={() => setQuickActionsOpen(true)} type="button">
          <AppIcon name="plus" size={27} />
        </button>
        <button className={`flex min-h-14 flex-col items-center justify-center gap-1 rounded-xl text-[10px] font-bold ${location.pathname.startsWith(mobileCustomersPath) ? "text-brand-600" : "text-slate-500"}`} onClick={() => goTo(mobileCustomersPath)} type="button">
          <AppIcon name="user" size={20} />
          مشتریان
        </button>
        <button className="flex min-h-14 flex-col items-center justify-center gap-1 rounded-xl text-[10px] font-bold text-slate-500" onClick={() => setMobileMenuOpen(true)} type="button">
          <AppIcon name="more" size={21} />
          بیشتر
        </button>
      </nav>
      {quickActionsOpen ? (
        <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-label="اقدام سریع">
          <button aria-label="بستن اقدامات سریع" className="absolute inset-0 cursor-default bg-slate-950/45" onClick={() => setQuickActionsOpen(false)} type="button" />
          <section className="relative w-full max-w-lg rounded-t-3xl bg-white p-5 shadow-2xl dark:bg-slate-900 sm:rounded-3xl" dir="rtl">
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-slate-200 dark:bg-slate-700 sm:hidden" />
            <div className="mb-4 flex items-center justify-between"><div><p className="text-xs font-bold text-brand-600">میز کار سریع</p><h2 className="mt-1 text-lg font-black text-slate-950 dark:text-white">چه کاری می‌خواهید انجام دهید؟</h2></div><button aria-label="بستن" className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => setQuickActionsOpen(false)} type="button"><AppIcon name="close" /></button></div>
            <div className="grid gap-3">
              {canCreateLead ? <button className="flex items-center gap-3 rounded-2xl border border-indigo-100 bg-indigo-50 p-4 text-right transition hover:border-indigo-300 dark:border-indigo-900/70 dark:bg-indigo-950/30" onClick={() => { setQuickActionsOpen(false); setQuickLeadOpen(true); }} type="button"><span className="grid size-11 place-items-center rounded-xl bg-indigo-600 text-white"><AppIcon name="trend" size={20} /></span><span><span className="block text-sm font-black text-slate-950 dark:text-white">ثبت سریع سرنخ</span><span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">شماره همراه، نام و ثبت فوری</span></span></button> : null}
              {canUseRetailDesk ? <button className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-right transition hover:border-emerald-300 dark:border-emerald-900/70 dark:bg-emerald-950/30" onClick={() => { setQuickActionsOpen(false); goTo("/retail?mode=introduce"); }} type="button"><span className="grid size-11 place-items-center rounded-xl bg-emerald-600 text-white"><AppIcon name="send" size={20} /></span><span><span className="block text-sm font-black text-slate-950 dark:text-white">معرفی سریع کالا</span><span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">انتخاب کالا و ارسال صفحه شخصی مشتری</span></span></button> : null}
              {canUseRetailDesk ? <button className="flex items-center gap-3 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-right transition hover:border-amber-300 dark:border-amber-900/70 dark:bg-amber-950/30" onClick={() => { setQuickActionsOpen(false); goTo("/retail?mode=sale"); }} type="button"><span className="grid size-11 place-items-center rounded-xl bg-amber-500 text-white"><AppIcon name="check" size={20} /></span><span><span className="block text-sm font-black text-slate-950 dark:text-white">فروش سریع</span><span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">ثبت یک‌کلیکی خرید و پیام تشکر</span></span></button> : null}
              {!canCreateLead && !canUseRetailDesk ? <p className="rounded-2xl bg-slate-50 p-4 text-center text-sm text-slate-500 dark:bg-slate-800 dark:text-slate-300">اقدام سریعی برای سطح دسترسی شما تعریف نشده است.</p> : null}
            </div>
          </section>
        </div>
      ) : null}
      {mobileSearchOpen ? (
        <div className="fixed inset-0 z-[70] bg-slate-950/45 p-4 pt-[max(env(safe-area-inset-top),1rem)] backdrop-blur-[1px] md:hidden" role="dialog" aria-modal="true" aria-label="جست‌وجو در CRM">
          <section className="mx-auto mt-14 w-full max-w-lg rounded-3xl bg-white p-4 shadow-2xl dark:bg-slate-900" dir="rtl">
            <div className="mb-4 flex items-center justify-between"><h2 className="text-base font-black text-slate-950 dark:text-white">جست‌وجو در CRM</h2><button aria-label="بستن" className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => setMobileSearchOpen(false)} type="button"><AppIcon name="close" size={19} /></button></div>
            <GlobalCrmSearch
              autoFocus
              canSearchAccounts={hasPermission(permissions, "accounts.read") && enabledFeatures.has(tenantFeatureCodes.organizations)}
              canSearchContacts={hasPermission(permissions, "contacts.read") && enabledFeatures.has(tenantFeatureCodes.customers)}
              canSearchLeads={hasPermission(permissions, "leads.read") && enabledFeatures.has(tenantFeatureCodes.leads)}
              className="w-full"
              onResultSelected={() => setMobileSearchOpen(false)}
            />
          </section>
        </div>
      ) : null}
      <QuickLeadModal open={quickLeadOpen} onClose={() => setQuickLeadOpen(false)} />
      <GuidanceAssistant />
    </div>
  );
};
